import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { BrokerService } from '../../../services/broker/broker.service';
import { ApiServiceService } from '../../../services/common/api-service.service';
import { environment } from '../../../../environments/environment';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-broker-dashboard',
  templateUrl: './broker-dashboard.component.html',
  styleUrls: ['./broker-dashboard.component.scss']
})
export class BrokerDashboardComponent implements OnInit {
  broker: any = null;
  activeTab: 'candidates' | 'commissions' | 'bank' | 'leads' | 'plans' = 'candidates';
  
  loading = true;
  error = '';
  successMsg = '';

  // Data lists
  candidates: any[] = [];
  commissions: any[] = [];
  leads: any[] = [];
  canViewContacts: boolean = false;
  currentBrokerPlan: string = 'FREE_BROKER';
  brokerPlans: any[] = [];
  loadingLeads = false;
  loadingPlans = false;
  subscribingPlan = false;
  showNotificationsDropdown = false;
  dismissLeadAlert = false;

  get newLeads(): any[] {
    return this.leads.filter(l => l.status === 'New');
  }

  get newLeadsCount(): number {
    return this.newLeads.length;
  }

  toggleNotifications(): void {
    this.showNotificationsDropdown = !this.showNotificationsDropdown;
  }

  viewLeadFromNotification(lead: any): void {
    this.activeTab = 'leads';
    this.showNotificationsDropdown = false;
    this.dismissLeadAlert = true;
  }

  // Masters for candidate creation
  religions: any[] = [];
  castes: any[] = [];
  allCastes: any[] = [];
  subCastes: any[] = [];
  allSubCastes: any[] = [];
  educations: any[] = [];
  occupations: any[] = [];
  incomes: any[] = [];
  cities: any[] = [];
  states: any[] = [];
  languages: any[] = [];
  gothrams: any[] = [];
  natchras: any[] = [];
  rasis: any[] = [];
  additionalIncomeSources = ['Rental Income', 'Agriculture', 'Business', 'Investments', 'Freelancing', 'Other (Specify / Type Custom)'];



  // Modals
  showAddCandidateModal = false;
  activeModalTab: string = 'basic';
  newCandidate: any = {};
  creatingCandidate = false;

  showEditModal = false;
  editingCandidate: any = null;
  editForm: any = {};
  savingEdit = false;

  showPayoutModal = false;
  payoutAmount: number = 0;
  requestingPayout = false;

  showViewersModal = false;
  selectedCandidateForViews: any = null;
  candidateViewsData: any = null;
  loadingViews = false;
  showOtpModal = false;
  otpProfile: any = null;
  generatedOtp: string = '';

  // Bank form
  bankDetails = {
    account_holder: '',
    account_number: '',
    ifsc_code: '',
    upi_id: ''
  };
  savingBank = false;
  maxDate: string = '';

  constructor(
    private brokerService: BrokerService,
    private apiService: ApiServiceService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const today = new Date();
    const maxYear = today.getFullYear() - 18;
    this.maxDate = new Date(maxYear, today.getMonth(), today.getDate()).toISOString().split('T')[0];
    this.loadDashboardData();
    this.loadMasters();
  }

  isUnderAge(dob: string): boolean {
    if (!dob) return false;
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return true;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const mDiff = today.getMonth() - birthDate.getMonth();
    if (mDiff < 0 || (mDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age < 18;
  }

  loadDashboardData(): void {
    this.loading = true;
    this.brokerService.getProfile().subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.broker = res.data;
          this.bankDetails = { ...(res.data.bank_details || {}) };
          this.loadCandidates();
          this.loadCommissions();
          this.loadLeads();
          this.loadBrokerPlans();
        } else {
          this.error = 'Failed to load profile';
          this.loading = false;
        }
      },
      error: (err) => {
        if (err.status === 401 || err.status === 403) {
          this.brokerService.logout();
          this.router.navigate(['/broker/login']);
        } else {
          this.error = 'Error connecting to server';
          this.loading = false;
        }
      }
    });
  }

  loadCandidates(): void {
    this.brokerService.getCandidates().subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.candidates = res.data?.profiles || [];
          if (this.broker) {
            if (!this.broker.stats) this.broker.stats = {};
            this.broker.stats.total_candidates = this.candidates.length;
          }
        }
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  loadCommissions(): void {
    this.brokerService.getCommissions().subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.commissions = res.data || [];
        }
      }
    });
  }

  loadMasters(): void {
    this.apiService.getReligions().subscribe((res: any) => { if (res.data) this.religions = res.data; });
    this.apiService.getCastes().subscribe((res: any) => {
      if (res.data) {
        this.allCastes = res.data;
        this.castes = [...this.allCastes];
      }
    });
    this.apiService.getSubCastes().subscribe((res: any) => {
      if (res.data) {
        this.allSubCastes = res.data;
        this.subCastes = [...this.allSubCastes];
      }
    });
    this.apiService.getEducation().subscribe((res: any) => { if (res.data) this.educations = res.data; });
    this.apiService.getOccupation().subscribe((res: any) => { if (res.data) this.occupations = res.data; });
    this.apiService.getIncome().subscribe((res: any) => { if (res.data) this.incomes = res.data; });
    this.apiService.getCity().subscribe((res: any) => { if (res.data) this.cities = res.data; });
    this.apiService.getState().subscribe((res: any) => { if (res.data) this.states = res.data; });
    this.apiService.getLanguages().subscribe((res: any) => { if (res.data) this.languages = res.data; });
    this.apiService.getGothrams().subscribe((res: any) => { if (res.data) this.gothrams = res.data; });
    this.apiService.getNatchra().subscribe((res: any) => { if (res.data) this.natchras = res.data; });
    this.apiService.getRasi().subscribe((res: any) => { if (res.data) this.rasis = res.data; });
  }

  onReligionChange(): void {
    if (!this.newCandidate.religion_id) {
      this.castes = [...this.allCastes];
      this.subCastes = [...this.allSubCastes];
      return;
    }
    this.castes = this.allCastes.filter(c => c.religion_id === this.newCandidate.religion_id || !c.religion_id);
    this.newCandidate.caste_id = '';
    this.newCandidate.sub_caste_id = '';
    this.onCasteChange();
  }

  onCasteChange(): void {
    if (!this.newCandidate.caste_id) {
      this.subCastes = [...this.allSubCastes];
      return;
    }
    this.subCastes = this.allSubCastes.filter(sc => sc.caste_id === this.newCandidate.caste_id || !sc.caste_id);
    this.newCandidate.sub_caste_id = '';
  }

  isHindu(religionId: string): boolean {
    if (!religionId) return false;
    const rel = this.religions.find(r => r._id === religionId);
    return (rel?.religion_nme || rel?.religion_name || '').toLowerCase() === 'hindu';
  }

  onPhotoSelected(event: any, isEdit: boolean = false): void {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        if (isEdit) {
          this.editForm.base64Image = reader.result as string;
          this.editForm.photoPreview = reader.result as string;
        } else {
          this.newCandidate.base64Image = reader.result as string;
          this.newCandidate.photoPreview = reader.result as string;
        }
      };
      reader.readAsDataURL(file);
    }
  }

  nextTab(): void {
    const tabs = ['basic', 'religion', 'personal', 'professional', 'family', 'horoscope'];
    const idx = tabs.indexOf(this.activeModalTab);
    if (idx < tabs.length - 1) {
      this.activeModalTab = tabs[idx + 1];
    }
  }

  prevTab(): void {
    const tabs = ['basic', 'religion', 'personal', 'professional', 'family', 'horoscope'];
    const idx = tabs.indexOf(this.activeModalTab);
    if (idx > 0) {
      this.activeModalTab = tabs[idx - 1];
    }
  }

  // Candidate creation
  openAddCandidateModal(): void {
    this.showAddCandidateModal = true;
    this.activeModalTab = 'basic';
    this.newCandidate = {
      first_name: '', last_name: '', gender: 'Male', date_of_birth: '', phone: '', email: '',
      mother_lang_id: '',
      religion_id: '', caste_id: '', sub_caste_id: '', gothrm_id: '', dosham: 'No', want_same_community: true,
      marital_status: 'Never Married', height: 165, weight: 60, physical_status: 'Normal', food: 'Vegetarian', body_type: 'Average', about_me: '',
      education_id: '', education_detail: '', occupation_id: '', occupation_detail: '', annual_salary: '', income_id: '',
      additional_income_source: '', additional_annual_income: null,
      city: '', state: '',
      family_status: 'Middle Class', family_type: 'Nuclear', family_values: 'Traditional', father_name: '', mother_name: '', no_of_brothers: 0, no_of_sisters: 0,
      base64Image: '', photoPreview: '',
      natchr_id: '', rasi_id: '', time_of_birth: '', horoscopeFile: null, horoscopePreview: null, horoscopeVisibility: 'Accepted Matches Only'
    };
  }

  closeAddCandidateModal(): void {
    this.showAddCandidateModal = false;
  }

  submitCandidate(): void {
    if (!this.newCandidate.first_name || !this.newCandidate.gender) {
      alert('First Name and Gender are required');
      return;
    }
    if (!this.newCandidate.date_of_birth) {
      alert('Date of Birth is required');
      return;
    }
    if (this.isUnderAge(this.newCandidate.date_of_birth)) {
      alert('Invalid Date of Birth: Candidate must be at least 18 years old. Please enter the correct birth year.');
      return;
    }
    if (this.isHindu(this.newCandidate.religion_id)) {
      if (!this.newCandidate.gothrm_id) {
        alert('Gothram is required for Hindu candidates');
        return;
      }
      if (!this.newCandidate.dosham) {
        alert('Dosham Status is required for Hindu candidates');
        return;
      }
      if (!this.newCandidate.natchr_id) {
        alert('Star / Nakshatra is required for Hindu candidates');
        return;
      }
      if (!this.newCandidate.rasi_id) {
        alert('Raasi / Moon Sign is required for Hindu candidates');
        return;
      }
    }
    this.creatingCandidate = true;
    const payload = this.buildFullPayload(this.newCandidate);
    this.brokerService.createCandidate(payload).subscribe({
      next: (res) => {
        this.creatingCandidate = false;
        if (res.status === 'success') {
          const newProfileId = res.data?._id || res.data?.id;
          if (this.newCandidate.horoscopeFile && newProfileId) {
            const formData = new FormData();
            formData.append('horoscope', this.newCandidate.horoscopeFile);
            formData.append('visibility', this.newCandidate.horoscopeVisibility || 'Accepted Matches Only');
            this.apiService.uploadHoroscope(newProfileId, formData).subscribe({
              next: () => {
                this.showSuccess('Candidate full bio-data and horoscope created successfully!');
                this.closeAddCandidateModal();
                this.loadDashboardData();
              },
              error: () => {
                this.showSuccess('Candidate created, but horoscope upload failed.');
                this.closeAddCandidateModal();
                this.loadDashboardData();
              }
            });
          } else {
            this.showSuccess('Candidate full bio-data created and published successfully!');
            this.closeAddCandidateModal();
            this.loadDashboardData();
          }
        } else {
          alert(res.message || 'Error creating candidate');
        }
      },
      error: (err) => {
        this.creatingCandidate = false;
        alert(err.error?.message || 'Error creating candidate');
      }
    });
  }

  // Candidate edit
  openEditModal(profile: any): void {
    this.editingCandidate = profile;
    this.activeModalTab = 'basic';
    this.editForm = {
      first_name: profile.basic_info?.first_name || '',
      last_name: profile.basic_info?.last_name || '',
      gender: profile.basic_info?.gender || 'Male',
      date_of_birth: profile.basic_info?.date_of_birth ? profile.basic_info.date_of_birth.substring(0, 10) : '',
      mobile: profile.basic_info?.mobile || profile.basic_info?.phone || '',
      email: profile.basic_info?.email || '',
      mother_lang_id: profile.basic_info?.mother_lang_id?._id || profile.basic_info?.mother_lang_id || '',

      religion_id: profile.religion_info?.religion_id?._id || profile.religion_info?.religion_id || '',
      caste_id: profile.religion_info?.caste_id?._id || profile.religion_info?.caste_id || '',
      sub_caste_id: profile.religion_info?.sub_caste_id?._id || profile.religion_info?.sub_caste_id || '',
      gothrm_id: profile.religion_info?.gothrm_id?._id || profile.religion_info?.gothrm_id || '',
      dosham: profile.religion_info?.dosham || 'No',
      want_same_community: profile.religion_info?.want_same_community !== undefined ? profile.religion_info.want_same_community : true,

      marital_status: profile.personal_info?.marital_status || profile.basic_info?.marital_status || 'Never Married',
      height: profile.personal_info?.height || 165,
      weight: profile.personal_info?.weight || 60,
      physical_status: profile.personal_info?.physical_status || 'Normal',
      food: (profile.personal_info?.food === 'Non-Vegetarian' ? 'Non-vegetarian' : profile.personal_info?.food) || 'Vegetarian',
      body_type: profile.personal_info?.body_type || 'Average',
      about_me: profile.personal_info?.about_me || '',

      education_id: profile.professional_info?.education_id?._id || profile.professional_info?.education_id || '',
      education_detail: profile.professional_info?.education_detail || profile.professional_info?.education_id?.education_name || '',
      occupation_id: profile.professional_info?.occupation_id?._id || profile.professional_info?.occupation_id || '',
      occupation_detail: profile.professional_info?.occupation_detail || '',
      annual_salary: profile.professional_info?.annual_salary || '',
      income_id: profile.professional_info?.income_id?._id || profile.professional_info?.income_id || '',
      additional_income_source: profile.professional_info?.additional_income_source || '',
      additional_annual_income: profile.professional_info?.additional_annual_income ? Number(profile.professional_info.additional_annual_income) : null,

      city: profile.residing_location?.city_id?._id || profile.residing_location?.city_id || profile.residing_location?.city || '',
      state: profile.residing_location?.state_id?._id || profile.residing_location?.state_id || profile.residing_location?.state || '',

      family_status: profile.family_info?.family_status || 'Middle Class',
      family_type: profile.family_info?.family_type || 'Nuclear',
      family_values: profile.family_info?.family_values || 'Traditional',
      father_name: profile.family_info?.father_name || '',
      mother_name: profile.family_info?.mother_name || '',
      no_of_brothers: profile.family_info?.no_of_brothers || 0,
      no_of_sisters: profile.family_info?.no_of_sisters || 0,

      base64Image: '',
      photoPreview: '',
      natchr_id: profile.horoscope_info?.natchr_id?._id || profile.horoscope_info?.natchr_id || '',
      rasi_id: profile.horoscope_info?.rasi_id?._id || profile.horoscope_info?.rasi_id || '',
      time_of_birth: profile.horoscope_info?.time_of_birth || '',
      horoscopeFile: null,
      horoscopePreview: null,
      horoscopeVisibility: profile.horoscope_visibility || profile.horoscope_info?.horoscope_visibility || 'Accepted Matches Only',
      existingHoroscopeName: profile.horoscope_file_name || profile.horoscope_info?.horoscope_file_name || '',
      existingHoroscopeUrl: profile.horoscope_file_url || profile.horoscope_info?.horoscope_file_url || ''
    };
    this.showEditModal = true;
    this.onEditReligionChange();
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editingCandidate = null;
  }

  onEditReligionChange(): void {
    if (!this.editForm.religion_id) {
      this.castes = [...this.allCastes];
      this.subCastes = [...this.allSubCastes];
      return;
    }
    this.castes = this.allCastes.filter(c => c.religion_id === this.editForm.religion_id || !c.religion_id);
    this.onEditCasteChange();
  }

  onEditCasteChange(): void {
    if (!this.editForm.caste_id) {
      this.subCastes = [...this.allSubCastes];
      return;
    }
    this.subCastes = this.allSubCastes.filter(sc => sc.caste_id === this.editForm.caste_id || !sc.caste_id);
  }

  getHoroscopeViewUrl(profileId: string): string {
    return `${environment.apiUrl}/profile_mgmt/horoscope/view/${profileId}`;
  }

  onHoroscopeSelected(event: any, isEdit: boolean = false): void {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert('Horoscope file size must be less than 10MB');
      return;
    }
    const target = isEdit ? this.editForm : this.newCandidate;
    target.horoscopeFile = file;
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = e => target.horoscopePreview = reader.result;
      reader.readAsDataURL(file);
    } else {
      target.horoscopePreview = 'PDF/Doc';
    }
  }

  removeHoroscope(isEdit: boolean = false): void {
    const target = isEdit ? this.editForm : this.newCandidate;
    target.horoscopeFile = null;
    target.horoscopePreview = null;
  }

  saveCandidateEdit(): void {
    if (!this.editingCandidate) return;
    if (this.editForm.date_of_birth && this.isUnderAge(this.editForm.date_of_birth)) {
      alert('Invalid Date of Birth: Candidate must be at least 18 years old. Please enter the correct birth year.');
      return;
    }
    if (this.isHindu(this.editForm.religion_id)) {
      if (!this.editForm.gothrm_id) {
        alert('Gothram is required for Hindu candidates');
        return;
      }
      if (!this.editForm.dosham) {
        alert('Dosham Status is required for Hindu candidates');
        return;
      }
      if (!this.editForm.natchr_id) {
        alert('Star / Nakshatra is required for Hindu candidates');
        return;
      }
      if (!this.editForm.rasi_id) {
        alert('Raasi / Moon Sign is required for Hindu candidates');
        return;
      }
    }
    this.savingEdit = true;
    const payload = this.buildFullPayload(this.editForm);
    this.brokerService.updateCandidate(this.editingCandidate._id, payload).subscribe({
      next: (res) => {
        this.savingEdit = false;
        if (res.status === 'success') {
          if (this.editForm.horoscopeFile && this.editingCandidate?._id) {
            const formData = new FormData();
            formData.append('horoscope', this.editForm.horoscopeFile);
            formData.append('visibility', this.editForm.horoscopeVisibility || 'Accepted Matches Only');
            this.apiService.uploadHoroscope(this.editingCandidate._id, formData).subscribe({
              next: () => {
                this.showSuccess('Candidate updated with new horoscope successfully!');
                this.closeEditModal();
                this.loadCandidates();
              },
              error: () => {
                this.showSuccess('Candidate updated, but horoscope upload failed.');
                this.closeEditModal();
                this.loadCandidates();
              }
            });
          } else {
            this.showSuccess('Candidate full bio-data updated successfully!');
            this.closeEditModal();
            this.loadCandidates();
          }
        } else {
          alert(res.message || 'Error updating candidate');
        }
      },
      error: (err) => {
        this.savingEdit = false;
        alert(err.error?.message || 'Error updating candidate');
      }
    });
  }

  buildFullPayload(form: any): any {
    return {
      first_name: form.first_name,
      last_name: form.last_name,
      gender: form.gender,
      date_of_birth: form.date_of_birth,
      phone: form.phone || form.mobile,
      email: form.email,
      religion_id: form.religion_id || null,
      caste_id: form.caste_id || null,
      sub_caste_id: form.sub_caste_id || null,
      city: form.city,
      state: form.state,
      base64Image: form.base64Image || null,
      basic_info: {
        first_name: form.first_name,
        last_name: form.last_name,
        gender: form.gender,
        date_of_birth: form.date_of_birth,
        mobile: form.phone || form.mobile,
        email: form.email,
        mother_lang_id: form.mother_lang_id || null,
        profile_created_by: 'Broker',
        marital_status: form.marital_status
      },
      religion_info: {
        religion_id: form.religion_id || null,
        caste_id: form.caste_id || null,
        sub_caste_id: form.sub_caste_id || null,
        gothrm_id: form.gothrm_id || '',
        dosham: form.dosham || 'No',
        want_same_community: form.want_same_community
      },
      personal_info: {
        marital_status: form.marital_status,
        height: form.height || null,
        weight: form.weight || null,
        physical_status: form.physical_status || 'Normal',
        food: form.food || 'Vegetarian',
        body_type: form.body_type || 'Average',
        about_me: form.about_me || '',
        base64Image: form.base64Image || null
      },
      professional_info: {
        education_id: form.education_id || null,
        education_detail: form.education_detail || '',
        occupation_id: form.occupation_id || null,
        occupation_detail: form.occupation_detail || '',
        annual_salary: form.annual_salary || '',
        income_id: form.income_id || null,
        additional_income_source: form.additional_income_source || '',
        additional_annual_income: form.additional_annual_income ? Number(form.additional_annual_income) : null
      },
      residing_location: {
        city_id: form.city || null,
        state_id: form.state || null,
        city: form.city || '',
        state: form.state || '',
        country: 'India'
      },
      family_info: {
        family_status: form.family_status || 'Middle Class',
        family_type: form.family_type || 'Nuclear',
        family_values: form.family_values || 'Traditional',
        father_name: form.father_name || '',
        mother_name: form.mother_name || '',
        no_of_brothers: form.no_of_brothers || 0,
        no_of_sisters: form.no_of_sisters || 0
      },
      horoscope_info: {
        time_of_birth: form.time_of_birth || '',
        natchr_id: form.natchr_id || null,
        rasi_id: form.rasi_id || null
      }
    };
  }

  // Payout request
  openPayoutModal(): void {
    this.showPayoutModal = true;
    this.payoutAmount = this.broker?.stats?.wallet_balance || 0;
  }

  closePayoutModal(): void {
    this.showPayoutModal = false;
  }

  submitPayout(): void {
    if (this.payoutAmount <= 0) return;
    this.requestingPayout = true;
    this.brokerService.requestPayout(this.payoutAmount).subscribe({
      next: (res) => {
        this.requestingPayout = false;
        if (res.status === 'success') {
          this.showSuccess(res.message || 'Payout request initiated');
          this.closePayoutModal();
          this.loadDashboardData();
        } else {
          alert(res.message || 'Error requesting payout');
        }
      },
      error: (err) => {
        this.requestingPayout = false;
        alert(err.error?.message || 'Error requesting payout');
      }
    });
  }

  // Bank details save
  saveBankDetails(): void {
    this.savingBank = true;
    this.brokerService.updateBankDetails(this.bankDetails).subscribe({
      next: (res) => {
        this.savingBank = false;
        if (res.status === 'success') {
          this.showSuccess('Bank and UPI details updated successfully!');
        }
      },
      error: () => {
        this.savingBank = false;
        alert('Error saving bank details');
      }
    });
  }

  // Initiate Profile Claim OTP
  initiateClaim(profile: any): void {
    if (!confirm(`Generate handover OTP for ${profile.basic_info?.first_name}?`)) return;
    this.brokerService.initiateClaim(profile._id).subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.otpProfile = profile;
          this.generatedOtp = res.data?.dev_otp || 'Sent via SMS';
          this.showOtpModal = true;
        } else {
          alert(res.message || 'Error generating OTP');
        }
      },
      error: (err) => {
        alert(err.error?.message || 'Error initiating claim');
      }
    });
  }

  closeOtpModal(): void {
    this.showOtpModal = false;
    this.otpProfile = null;
  }

  logout(): void {
    this.brokerService.logout();
    this.router.navigate(['/broker/login']);
  }

  showSuccess(msg: string): void {
    this.successMsg = msg;
    setTimeout(() => { this.successMsg = ''; }, 4000);
  }

  loadLeads(): void {
    this.loadingLeads = true;
    this.brokerService.getLeads().subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.leads = res.data?.leads || [];
          this.canViewContacts = res.data?.can_view_contacts || false;
          this.currentBrokerPlan = res.data?.current_plan || 'FREE_BROKER';
        }
        this.loadingLeads = false;
      },
      error: () => { this.loadingLeads = false; }
    });
  }

  loadBrokerPlans(): void {
    this.loadingPlans = true;
    this.brokerService.getBrokerPlans().subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.brokerPlans = res.data || [];
        }
        this.loadingPlans = false;
      },
      error: () => { this.loadingPlans = false; }
    });
  }

  onUpdateLeadStatus(lead: any, newStatus: string): void {
    this.brokerService.updateLeadStatus(lead._id, newStatus, lead.notes).subscribe({
      next: (res) => {
        if (res.status === 'success') {
          lead.status = newStatus;
          this.showSuccess(`Lead status updated to ${newStatus}`);
        }
      }
    });
  }

  loadRazorpaySDK(): Promise<boolean> {
    return new Promise((resolve) => {
      if ((window as any).Razorpay) {
        return resolve(true);
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  onSubscribeBrokerPlan(plan: any): void {
    if (this.subscribingPlan) return;

    Swal.fire({
      title: `Upgrade to ${plan.display_name}?`,
      text: `You are initiating a secure checkout for the ${plan.display_name} subscription (₹${plan.price}).`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Proceed to Payment',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#800020'
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      Swal.fire({
        title: 'Initializing Checkout...',
        text: 'Connecting to Razorpay secure gateway...',
        allowOutsideClick: false,
        didOpen: () => { Swal.showLoading(); }
      });

      await this.loadRazorpaySDK();

      this.subscribingPlan = true;
      this.brokerService.createOrder(plan.plan_code).subscribe({
        next: (orderRes) => {
          Swal.close();
          if (!orderRes || orderRes.status !== 'success') {
            Swal.fire('Checkout Error', orderRes?.message || 'Could not initiate transaction', 'error');
            this.subscribingPlan = false;
            return;
          }

          const orderData = orderRes.data;
          const options = {
            key: orderData.key_id || 'rzp_test_1234567890',
            amount: orderData.amount,
            currency: orderData.currency || 'INR',
            name: 'Weddingly Broker',
            description: `${plan.display_name} Membership Upgrade`,
            order_id: orderData.order_id,
            handler: (response: any) => {
              Swal.fire({
                title: 'Verifying Signature...',
                text: 'Cryptographic validation in progress...',
                allowOutsideClick: false,
                didOpen: () => { Swal.showLoading(); }
              });

              this.brokerService.verifyPayment({
                razorpay_order_id: response.razorpay_order_id || orderData.order_id,
                razorpay_payment_id: response.razorpay_payment_id || `pay_rzp_${Date.now()}`,
                razorpay_signature: response.razorpay_signature || 'test_signature_bypass'
              }).subscribe({
                next: (verifyRes) => {
                  this.subscribingPlan = false;
                  if (verifyRes && verifyRes.status === 'success') {
                    this.currentBrokerPlan = plan.plan_code;
                    if (this.broker) this.broker.current_plan = plan.plan_code;
                    this.loadLeads();
                    this.loadDashboardData();
                    Swal.fire({
                      title: '🎉 Upgrade Successful!',
                      text: `Your ${plan.display_name} plan is now officially ACTIVE.`,
                      icon: 'success',
                      confirmButtonColor: '#800020'
                    });
                  } else {
                    Swal.fire('Verification Failed', 'Transaction signature verification failed.', 'error');
                  }
                },
                error: (vErr) => {
                  this.subscribingPlan = false;
                  Swal.fire('Error', vErr.error?.message || 'Verification service error', 'error');
                }
              });
            },
            prefill: {
              name: this.broker?.name || 'Weddingly Broker',
              email: this.broker?.email || '',
              contact: this.broker?.mobile || ''
            },
            theme: {
              color: '#800020'
            },
            modal: {
              ondismiss: () => {
                this.subscribingPlan = false;
                Swal.fire('Checkout Cancelled', 'You closed the payment popup.', 'info');
              }
            }
          };

          if ((window as any).Razorpay) {
            const rzp = new (window as any).Razorpay(options);
            rzp.open();
          } else {
            // Simulated fallback if browser blocked external Razorpay JS loading
            options.handler({
              razorpay_order_id: orderData.order_id,
              razorpay_payment_id: `pay_rzp_sim_${Date.now()}`,
              razorpay_signature: 'simulated_test_sig'
            });
          }
        },
        error: (err) => {
          this.subscribingPlan = false;
          Swal.fire('Checkout Error', err.error?.message || 'Error upgrading plan', 'error');
        }
      });
    });
  }

  openCandidateViewersModal(candidate: any): void {
    this.selectedCandidateForViews = candidate;
    this.showViewersModal = true;
    this.loadingViews = true;
    this.candidateViewsData = null;
    this.brokerService.getCandidateViews(candidate._id).subscribe({
      next: (res) => {
        if (res.status === 'success') {
          this.candidateViewsData = res.data;
        }
        this.loadingViews = false;
      },
      error: () => {
        this.loadingViews = false;
        alert('Could not fetch viewers right now');
      }
    });
  }

  closeCandidateViewersModal(): void {
    this.showViewersModal = false;
    this.selectedCandidateForViews = null;
    this.candidateViewsData = null;
  }

  isCustomAddIncome(val: string | null | undefined): boolean {
    if (!val) return false;
    const standard = ['Rental Income', 'Agriculture', 'Business', 'Investments', 'Freelancing'];
    return !standard.includes(val);
  }

  onCustomCandidateIncomeInput(event: any, isEdit: boolean): void {
    const customVal = event?.target?.value || '';
    if (isEdit) {
      this.editForm.additional_income_source = customVal || 'Other (Specify / Type Custom)';
    } else {
      this.newCandidate.additional_income_source = customVal || 'Other (Specify / Type Custom)';
    }
  }
}
